/**
 * Pure derivation of one acquisition channel's live figures (ad-spend today + blended CPA)
 * from its raw inputs. Kept free of React/store imports so the missing-CPA path is unit
 * tested without a render. Consumed by {@link useAcquisitionChannelFigures}.
 */
import { type Bucket, sumBuckets } from '../home/stats-shape';
import { buildAdSpendStat, markerText } from './performance-stats';

/** Real figures for one channel, keyed by `aft`. */
export type ChannelFigure = {
  /** Ad-spend today, rounded to whole currency (the "spent today" figure). */
  spent: number;
  /** Blended CPA as a raw number, or `null` when unavailable (errored / no FTDs) so a caller
   *  that divides by it (e.g. the modal's allocation weights) can guard instead of NaN-ing. */
  cpaValue: number | null;
  /** Display string matching the cards: `"$8 CPA"`, `"– CPA"`, `"N/A CPA"`, `"? CPA"`. */
  cpaLabel: string;
  /** Which stats line the modal renders for this channel: `'value'` shows the CPA;
   *  `'pending'` = spending today but no conversions yet (CPA not computable, do not show
   *  a placeholder dash); `'idle'` = no spend today (omit the CPA token entirely); `'error'`
   *  = the figure request errored (keep the N/A label). Lets the render pick a self-
   *  explanatory line instead of a bare `–` that reads as broken. */
  cpaState: 'value' | 'pending' | 'idle' | 'error';
};

/**
 * Derive one channel's figures from its raw inputs. `errored` short-circuits to `N/A`; zero
 * FTDs → an indeterminate `–` (and a `null` `cpaValue`); otherwise CPA is `cost / ftds`.
 * `cpaLabel` is produced by the exact same `buildAdSpendStat` path the cards use, so the two
 * never disagree.
 */
export const buildChannelFigure = (cost: number, ftdBuckets: Bucket[], errored: boolean): ChannelFigure => {
  const ftds = sumBuckets(ftdBuckets, ['ftds']);
  const stat = buildAdSpendStat('blended-cpa', {
    cost,
    baselineCost: cost,
    current: ftdBuckets,
    baseline: [],
    errored,
  });
  const cpaValue = !errored && ftds > 0 ? cost / ftds : null;
  const spent = Math.round(cost);
  // A missing CPA is TWO distinct states, not one: 'idle' (no spend today — nothing to
  // report, so the CPA token is dropped) vs 'pending' (money out today but no conversion
  // yet — CPA is coming). Both used to collapse to a bare '– CPA' that read as broken.
  const cpaState: ChannelFigure['cpaState'] = errored
    ? 'error'
    : cpaValue != null
      ? 'value'
      : spent > 0
        ? 'pending'
        : 'idle';
  return { spent, cpaValue, cpaLabel: `${markerText(stat)} CPA`, cpaState };
};
